import type { ApiResponse, ApiErrorResponse } from '@pgway/shared';
import { getAccessToken, setAccessToken, clearAuth } from './auth';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function refreshToken(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE_URL}/auth/refresh-token`, {
      method: 'POST',
      credentials: 'include', // send httpOnly cookie
    });

    if (!res.ok) {
      clearAuth();
      return false;
    }

    const data = await res.json();
    if (data.ok && data.data.accessToken) {
      setAccessToken(data.data.accessToken);
      return true;
    }

    clearAuth();
    return false;
  } catch {
    clearAuth();
    return false;
  }
}

async function handleRefresh(): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = refreshToken().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  // If 401, try refreshing the token
  if (res.status === 401 && token) {
    const refreshed = await handleRefresh();
    if (refreshed) {
      const newToken = getAccessToken();
      headers['Authorization'] = `Bearer ${newToken}`;
      res = await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers,
        credentials: 'include',
      });
    } else {
      // Redirect to login
      window.location.href = '/login';
      const errResponse: ApiErrorResponse = {
        ok: false,
        error: {
          status: 401,
          type: 'AUTH_ERROR',
          code: 'SESSION_EXPIRED',
          message: 'Session expired. Please log in again.',
        },
      };
      return errResponse;
    }
  }

  return res.json();
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, string>,
): Promise<ApiResponse<T>> {
  const url = params ? `${path}?${new URLSearchParams(params).toString()}` : path;
  return apiRequest<T>(url, { method: 'GET' });
}

export async function apiPost<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
  return apiRequest<T>(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
  return apiRequest<T>(path, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function apiPut<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
  return apiRequest<T>(path, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function apiDelete<T>(
  path: string,
  params?: Record<string, string>,
): Promise<ApiResponse<T>> {
  const url = params ? `${path}?${new URLSearchParams(params).toString()}` : path;
  return apiRequest<T>(url, { method: 'DELETE' });
}

export function adminCompanyPath(path: string, companyId: string): string {
  return '/admin/companies/' + companyId + path;
}
